import type { ContractBonus } from "@/components/bonus-repeater";

export type ContractTypeKey = "head_coach" | "assistant_coach" | "coordinator" | "interim";

export type TemplateBonus = Omit<ContractBonus, "id">;

export type BonusTemplate = {
  id: string;
  name: string;
  description: string;
  sports?: string[];
  contractTypes?: ContractTypeKey[];
  bonuses: TemplateBonus[];
};

export const BONUS_TEMPLATES: BonusTemplate[] = [
  {
    id: "football-head-coach",
    name: "Football — Head Coach",
    description: "Standard FBS football head coach incentives: conference title, CFP, bowl, academic.",
    sports: ["Football"],
    contractTypes: ["head_coach"],
    bonuses: [
      {
        type: "performance",
        label: "Conference Championship Win",
        amount: 250000,
        condition: "Win conference championship game",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "College Football Playoff Appearance",
        amount: 200000,
        condition: "Team selected for the College Football Playoff",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "National Championship Win",
        amount: 750000,
        condition: "Win the College Football National Championship",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "Bowl Game Appearance",
        amount: 50000,
        condition: "Team selected for an NCAA-sanctioned bowl game",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "academic",
        label: "Team APR ≥ 985",
        amount: 50000,
        condition: "Single-year Academic Progress Rate of 985 or higher",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "academic",
        label: "Graduation Success Rate ≥ 80%",
        amount: 50000,
        condition: "Reported GSR of 80% or higher",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
    ],
  },
  {
    id: "mbb-head-coach",
    name: "Men's Basketball — Head Coach",
    description: "Standard men's basketball head coach incentives: conference, NCAA tournament, Final Four.",
    sports: ["Men's Basketball"],
    contractTypes: ["head_coach"],
    bonuses: [
      {
        type: "performance",
        label: "Regular-Season Conference Championship",
        amount: 75000,
        condition: "Win or share regular-season conference title",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "Conference Tournament Championship",
        amount: 100000,
        condition: "Win conference postseason tournament",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "NCAA Tournament Appearance",
        amount: 75000,
        condition: "Team selected for NCAA Division I Men's Basketball Tournament",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "Sweet Sixteen",
        amount: 100000,
        condition: "Advance to NCAA Tournament Sweet Sixteen",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "Final Four",
        amount: 200000,
        condition: "Advance to NCAA Tournament Final Four",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "National Championship Win",
        amount: 500000,
        condition: "Win NCAA National Championship",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "academic",
        label: "Team APR ≥ 985",
        amount: 25000,
        condition: "Single-year Academic Progress Rate of 985 or higher",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
    ],
  },
  {
    id: "wbb-head-coach",
    name: "Women's Basketball — Head Coach",
    description: "Standard women's basketball head coach incentives: conference, NCAA tournament, Final Four.",
    sports: ["Women's Basketball"],
    contractTypes: ["head_coach"],
    bonuses: [
      {
        type: "performance",
        label: "Regular-Season Conference Championship",
        amount: 50000,
        condition: "Win or share regular-season conference title",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "Conference Tournament Championship",
        amount: 75000,
        condition: "Win conference postseason tournament",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "NCAA Tournament Appearance",
        amount: 50000,
        condition: "Team selected for NCAA Division I Women's Basketball Tournament",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "Sweet Sixteen",
        amount: 75000,
        condition: "Advance to NCAA Tournament Sweet Sixteen",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "Final Four",
        amount: 150000,
        condition: "Advance to NCAA Tournament Final Four",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "National Championship Win",
        amount: 350000,
        condition: "Win NCAA National Championship",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "academic",
        label: "Team APR ≥ 985",
        amount: 25000,
        condition: "Single-year Academic Progress Rate of 985 or higher",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
    ],
  },
  {
    id: "baseball-head-coach",
    name: "Baseball — Head Coach",
    description: "Standard baseball head coach incentives: conference, regional, College World Series.",
    sports: ["Baseball"],
    contractTypes: ["head_coach"],
    bonuses: [
      {
        type: "performance",
        label: "Conference Championship",
        amount: 25000,
        condition: "Win conference regular-season or tournament title",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "NCAA Regional Appearance",
        amount: 25000,
        condition: "Team selected for NCAA Regional",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "Super Regional Appearance",
        amount: 50000,
        condition: "Advance to NCAA Super Regional",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "College World Series",
        amount: 100000,
        condition: "Advance to College World Series in Omaha",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "National Championship Win",
        amount: 250000,
        condition: "Win NCAA Baseball National Championship",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "academic",
        label: "Team APR ≥ 985",
        amount: 15000,
        condition: "Single-year Academic Progress Rate of 985 or higher",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
    ],
  },
  {
    id: "generic-head-coach",
    name: "Generic — Head Coach",
    description: "Baseline incentive structure suitable for most head coach contracts.",
    contractTypes: ["head_coach"],
    bonuses: [
      {
        type: "signing",
        label: "Signing Bonus",
        amount: 50000,
        paymentTiming: "one_time_signing",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "Conference Championship",
        amount: 50000,
        condition: "Win conference championship",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "NCAA Postseason Appearance",
        amount: 25000,
        condition: "Team qualifies for NCAA postseason competition",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "academic",
        label: "Team APR ≥ 985",
        amount: 15000,
        condition: "Single-year Academic Progress Rate of 985 or higher",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "retention",
        label: "Retention Bonus",
        amount: 100000,
        condition: "On roster as head coach as of contract anniversary",
        paymentTiming: "annual",
        isRecurring: true,
        recurringFrequency: "Annually",
      },
    ],
  },
  {
    id: "generic-assistant",
    name: "Generic — Assistant Coach / Coordinator",
    description: "Baseline incentive structure for assistant coaches and coordinators.",
    contractTypes: ["assistant_coach", "coordinator"],
    bonuses: [
      {
        type: "performance",
        label: "Conference Championship",
        amount: 15000,
        condition: "Team wins conference championship",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "performance",
        label: "NCAA Postseason Appearance",
        amount: 10000,
        condition: "Team qualifies for NCAA postseason competition",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
      {
        type: "academic",
        label: "Team APR ≥ 985",
        amount: 5000,
        condition: "Single-year Academic Progress Rate of 985 or higher",
        paymentTiming: "on_trigger",
        isRecurring: false,
      },
    ],
  },
];

export function getApplicableTemplates(
  sport: string | undefined,
  contractType: ContractTypeKey | undefined,
): BonusTemplate[] {
  return BONUS_TEMPLATES.filter((t) => {
    const sportOk = !t.sports || (sport ? t.sports.includes(sport) : false);
    const typeOk = !t.contractTypes || (contractType ? t.contractTypes.includes(contractType) : true);
    return sportOk && typeOk;
  });
}
